import React, { useState, useEffect, useContext } from 'react';
import { Table, Button, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { collection, getDocs, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import EditPanierModal from './EditPanierModal';
import { UserContext } from '../../../contexts/UserContext';
import ReactPaginate from 'react-paginate';

const Panier = () => {
    const [paniers, setPaniers] = useState([]);
    const [plats, setPlats] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedPanier, setSelectedPanier] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [panierToDelete, setPanierToDelete] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage] = useState(5);
    const [selectedClient, setSelectedClient] = useState(''); // State for client filter
    const { user } = useContext(UserContext);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [paniersCollection, platsCollection, usersCollection] = [
                    collection(db, 'panier'),
                    collection(db, 'food-items'),
                    collection(db, 'user')
                ];
                const [platsSnapshot, usersSnapshot] = await Promise.all([
                    getDocs(platsCollection),
                    getDocs(usersCollection)
                ]);

                const platsList = platsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                const usersList = usersSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })).filter(user => user.role === 'pdqve0oMlifjYOu6p52Z'); // Filter users by role "client"

                setPlats(platsList);
                setUsers(usersList);
            } catch (error) {
                console.error("Error fetching data: ", error);
                setError("Failed to load data. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const paniersCollection = collection(db, 'panier');
        const unsubscribe = onSnapshot(paniersCollection, snapshot => {
            const paniersList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setPaniers(paniersList);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (paniers.length > 0 && plats.length > 0) {
            const updatedPaniers = paniers.map(panier => {
                const montant = panier.plat.reduce((total, platPanier) => {
                    const platDetails = plats.find(p => p.id === platPanier.id);
                    if (platDetails) {
                        return total + platDetails.prix * platPanier.quantite;
                    }
                    return total;
                }, 0);
                return { ...panier, montant };
            });
            setPaniers(updatedPaniers);
        }
    }, [paniers, plats]);

    const getUserDetails = (userId) => {
        const user = users.find(u => u.id === userId);
        return user ? { nom: user.nom, email: user.email } : { nom: 'Utilisateur inconnu', email: 'Email inconnu' };
    };

    const getPlatDetails = (platId) => {
        const plat = plats.find(p => p.id === platId);
        return plat ? plat.nom : 'Plat inconnu';
    };

    const handleEditClick = (panier) => {
        setSelectedPanier(panier);
        setShowEditModal(true);
    };

    const handleSave = async () => {
        setShowEditModal(false);
        // The onSnapshot will automatically handle updating paniers.
    };

    const handleDeleteClick = (panier) => {
        setPanierToDelete(panier);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (panierToDelete) {
            try {
                const panierRef = doc(db, 'panier', panierToDelete.id);
                await deleteDoc(panierRef);
                setPaniers(prevPaniers => prevPaniers.filter(panier => panier.id !== panierToDelete.id));
                setShowDeleteModal(false);
                setPanierToDelete(null);
            } catch (error) {
                console.error("Error deleting panier: ", error);
            }
        }
    };

    const updatePanierState = async () => {
        const paniersCollection = collection(db, 'panier');
        const paniersSnapshot = await getDocs(paniersCollection);
        const paniersList = paniersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        setPaniers(paniersList);
    };

    const handleClientFilterChange = (event) => {
        setSelectedClient(event.target.value);
        setCurrentPage(0); // Reset to first page after filtering
    };

    const filteredPaniers = selectedClient 
        ? paniers.filter(panier => panier.user === selectedClient) 
        : paniers;

    const indexOfLastPanier = (currentPage + 1) * itemsPerPage;
    const indexOfFirstPanier = indexOfLastPanier - itemsPerPage;
    const currentPaniers = filteredPaniers.slice(indexOfFirstPanier, indexOfLastPanier);

    const handlePageClick = (event) => {
        setCurrentPage(event.selected);
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div id="content-wrapper" className="d-flex flex-column ">
            <div id="content">
                <div className="container-fluid">
                    <h1 className="h3 mb-2 text-gray-800">Paniers</h1>
                    <br/>
                    <div className="card shadow mb-4">
                        <div className="card-header py-3 d-flex justify-content-between align-items-center">
                            <h6 className="m-0 font-weight-bold title-2">Paniers</h6>
                            <Form.Control 
                                as="select" 
                                value={selectedClient} 
                                onChange={handleClientFilterChange} 
                                className="w-25"
                            >
                                <option value="">Tous les clients</option>
                                {users.map(user => (
                                    <option key={user.id} value={user.id}>{user.nom}</option>
                                ))}
                            </Form.Control>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <Table bordered hover>
                                    <thead>
                                        <tr>
                                            <th>Panier</th>
                                            <th>Nom de client</th>
                                            <th>Email de client</th>
                                            <th>Date d'enregistrement</th>
                                            <th>Plats</th>
                                            <th>Montant</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentPaniers.map((panier) => {
                                            const userDetails = getUserDetails(panier.user);
                                            return (
                                                <tr key={panier.id}>
                                                    <td>#{panier.id}</td>
                                                    <td>{userDetails.nom}</td>
                                                    <td>{userDetails.email}</td>
                                                    <td>{panier.date_saved}</td>
                                                    <td>
                                                        {panier.plat && panier.plat.map((plat, index) => (
                                                            <p key={index}>{getPlatDetails(plat.id)} - Quantité: {plat.quantite}</p>
                                                        ))}
                                                    </td>
                                                    <td>{panier.montant ? panier.montant.toFixed(2) : 'Calcul en cours'} €</td>
                                                    <td className='wth-100'>
                                                        <Button variant="primary" className="btn btn-primary btn-circle btn-sm mr-2" onClick={() => handleEditClick(panier)}>
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </Button>
                                                        {user && user.role === 'Kuo8Wsgg6Q7l4ZGhbTf3' && (
                                                            <Button variant="btn btn-danger btn-circle btn-sm" onClick={() => handleDeleteClick(panier)}>
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                            {/* Pagination Controls */}
                            <ReactPaginate
                                previousLabel={"Précédent"}
                                nextLabel={"Suivant"}
                                breakLabel={"..."}
                                pageCount={Math.ceil(filteredPaniers.length / itemsPerPage)}
                                marginPagesDisplayed={2}
                                pageRangeDisplayed={10}
                                onPageChange={handlePageClick}
                                containerClassName={"pagination justify-content-start" }
                                pageClassName={"page-item"}
                                pageLinkClassName={"page-link"}
                                previousClassName={"page-item"}
                                previousLinkClassName={"page-link"}
                                nextClassName={"page-item"}
                                nextLinkClassName={"page-link"}
                                breakClassName={"page-item"}
                                breakLinkClassName={"page-link"}
                                activeClassName={"active"}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {showEditModal && (
                <EditPanierModal
                    show={showEditModal}
                    handleClose={() => setShowEditModal(false)}
                    panier={selectedPanier}
                    users={users}
                    plats={plats}
                    onSave={handleSave}
                    updatePanierState={updatePanierState}
                />
            )}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmation de suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>Êtes-vous sûr de vouloir supprimer ce panier ?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={confirmDelete}>
                        Supprimer
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Panier;
